import React, { useState, useEffect } from 'react';
import { Layout, Button, Tabs } from 'tea-component';
import { history, shallowEqual, useSelector } from 'umi';
import PublishModal from '../components/PublishModal';
import { getIsTemplatePageFn, safeParseBoolean } from '../CreateReport/utils';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { buriedPoint } from "@home/utils/buriedPoint";

import './viewPage.scss';
import { LOCAL_STROAGE_GAME_CODE_KEY, SYSTEM_NAME } from '@dbl/core/global';
import { storage } from "@/utils/utils";
import { useDashboardReportStore } from "@/pages/datalab/components/DashboardLayout/hooks";
import classNames from "classnames";
import DashboardLayout from "@/pages/datalab/components/DashboardLayout";
import { TableDataTab } from '@/pages/datalab/pages/WorkBench/constants';
import CopyDashboardModal, {
  ChartInDashboardCopyType
} from '@/pages/datalab/pages/WorkBench/components/CopyDashboardModal';

const { Body, Content } = Layout;

export default function ViewPages(props: any) {
  const gameCode = useSelector((state) => (state.datalab.gameCode), shallowEqual);
  const [dashboardInfo, setDashboardInfo] = useState<any>({});
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
  const [showCopy, setShowCopy] = useState<boolean>(false);
  const { commonFormatMessage } = useDblIntl('my_dashboard');
  // JSON.parse 兼容 旧路径
  const { dashboardId = '', dashboardName = '', isBack: isPathBack, tab, tabId } = props?.location?.query?.dashboardMsg ? JSON.parse(props?.location?.query?.dashboardMsg) : props?.location?.query;
  const isBack = safeParseBoolean(isPathBack);

  const {userId} = storage.getLocalStorageItem(LOCAL_STROAGE_GAME_CODE_KEY);
  
  // 报表显示的数据状态和逻辑
  const { 
    headerTabs,
    tabLayouts,
    currentTabId, 
    setCurrentTabId, 
    filters,
    userFilter,
    enableCollapseFilter,
    layouts,
    showContact,
    contactInfo, 
    dashboardInfoRunAsync, 
    dashboardInfoLoading,
    dashboardInfoError, 
    chartsInfoLoading,
    setChartsInfoLoading,
  } = useDashboardReportStore({system: SYSTEM_NAME, dashboardId, defaultTabId: tabId});
  // 发起报表信息请求
  useEffect(() => {
    dashboardId && dashboardInfoRunAsync({game_code: gameCode, id: dashboardId, system: SYSTEM_NAME}).then((res) => {
      if (!res.read) { // 如果用户对这个报表没有read权限, 将跳转到我的报表页面
        history.replace(`/${SYSTEM_NAME}/${gameCode}/WorkBench/MyDashboard`);
        return;
      }
  
      // 判断是否是模板报表
      const isTemplateReport = getIsTemplatePageFn(res?.category);
      // 有访问权限后再打点
      buriedPoint({
        pageId: isTemplateReport ? '601601' : '601301',
        pageName: dashboardName,
        extInfo: `{"pageName": "${dashboardName}", "pageNameCn": "", "id": "${dashboardId}"}`,
        pagePath: '/WorkBench/MyDashboard/ViewPage',
        uid: gameCode,
        typeId: 'datalab_pc_v2',
        menuId: '0'
      });
  
      const dashboardTitle = isTemplateReport ? res?.template_title : res?.dashboard_title;
      setDashboardInfo({edit: res?.edit, publish: res?.publish, publishStatus: res?.publish_status, dashboardTitle, isTemplateReport, exploratory: res?.exploratory, copy: res?.copy, category: res?.category });
      
    }).catch((e) => {
      console.error('报表预览页面报表信息请求错误', e);
    });
  }, [dashboardId]);
  
  const title = dashboardInfo.dashboardTitle ?? dashboardName;
  
  return (
    <Layout className={'dbl-force-bubble-popup'}>
      <Body>
        <Content>
          <Content.Header
            showBackButton
            onBackButtonClick={() => {
              history.push( `/datalab/${gameCode}/${ isBack ? 'Home' : 'WorkBench/MyDashboard' }`);
            }}
            title={title}
            operation={ 
              <>
                {dashboardInfo?.copy && (<Button
                  className="table-btn"
                  type={dashboardInfo?.edit ? 'weak' : 'primary'}
                  onClick={()=>{
                    setShowCopy(true);
                  }}
                >
                  {commonFormatMessage('copy')}
                </Button>)
                }

                {
                  dashboardInfo?.publishStatus !== 1 && dashboardInfo?.publish && <Button
                    type="weak"
                    onClick={() => {
                      setShowPublishModal(true);
                    }}
                  >
                    {commonFormatMessage('publish')}
                  </Button>
                }

                {
                  dashboardInfo?.edit && (<Button
                    type="primary"
                    onClick={() => {
                      history.push({
                        pathname: `/datalab/${gameCode}/WorkBench/MyDashboard/CreateDashboard`,
                        query: {
                          dashboardId: dashboardId || '',
                          dashboardName: title || '',
                          isTemplateReport: dashboardInfo?.isTemplateReport,
                          tab,
                          tabId,
                        }
                      });
                    }}
                  >
                    {commonFormatMessage('edit')}
                  </Button>)
                }

              </>
            }
            className={classNames({'dbl-dashboard-header-with-tabs': headerTabs.length > 0})}
          >
            <Tabs
              tabs={headerTabs}
              activeId={currentTabId}
              onActive={(tab) => {
                if (tab.id === currentTabId) return;
                setCurrentTabId(tab.id);
                // 切换页签 图表信息重新请求
                setChartsInfoLoading(true);
                history.replace({
                  pathname: history?.location?.pathname,
                  query: {
                    ...(history?.location?.query || {}),
                    tab: typeof tab?.label === 'string' ? tab?.label : tab?.id,
                    tabId: tab.id,
                  }
                });
              }}
              className={'dbl-dashboard-header-tabs'}
            />
          </Content.Header>
          <Content.Body className='dbl-custom-dashboard-view-box'>
            <DashboardLayout
              key={currentTabId}
              // className={'dbl-edit-dashboard'}
              gameCode={gameCode}
              dashboardId={dashboardId}
              tabLayouts={tabLayouts}
              filters={filters}
              userFilter={userFilter}
              enableCollapseFilter={enableCollapseFilter}
              layouts={layouts}
              loading={dashboardInfoLoading}
              error={dashboardInfoError}
              // 报表内渲染的图表信息请求加载态
              chartsInfoLoading={chartsInfoLoading}
              userId={userId}
              chartExplore={dashboardInfo.exploratory}
              showContact={showContact}
              contactInfo={contactInfo}
            />
          </Content.Body>
        </Content>
      </Body>
      <CopyDashboardModal
        gameCode={gameCode}
        dashboardId={dashboardId}
        visible={showCopy}
        onClose={()=>setShowCopy(false)}
        dashboardName={dashboardInfo.dashboardTitle ?? dashboardName}
        chartCopyType={dashboardInfo?.category === TableDataTab.Public ? ChartInDashboardCopyType.COPY : ChartInDashboardCopyType.REFER}
        isReplaceDashboard
        showContact={showContact}
      />
      {
        showPublishModal && <PublishModal
          publishId={dashboardId}
          dashboardName={dashboardName}
          closeFunc={() => { setShowPublishModal(false) }}
        />
      }
    </Layout>
  );
}
